// src/pages/ResetPasswordRequiredPage.jsx
import React, { useState } from "react";
import { Box, TextField, Button, Typography, Container, Paper, Alert } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { API_BASE_URL } from "../config/apiKey.js";
import LockOpenIcon from "@mui/icons-material/LockOpen";

export default function ResetPasswordRequiredPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromQuery = new URLSearchParams(location.search).get("email") || "";
  
  const [email, setEmail] = useState(emailFromQuery);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!email) {
      setError("Email is required");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      // Call a special endpoint for post-unlock password reset (no token needed)
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password-after-unlock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, newPassword: password }),
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess("Password updated successfully. You can now log in.");
        setTimeout(() => navigate("/login"), 2000);
      } else {
        setError(data.error || "Failed to reset password");
      }
    } catch (e) {
      setError(e.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "radial-gradient(circle at top, #1e3a8a 0%, #020617 45%, #000814 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(56, 189, 248, 0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(37, 99, 235, 0.08) 1px, transparent 1px)
          `,
          backgroundSize: "50px 50px",
          opacity: 0.3,
        }}
      />

      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper
            elevation={24}
            sx={{
              p: 4,
              bgcolor: "rgba(15, 23, 42, 0.96)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(37, 99, 235, 0.4)",
              borderRadius: 4,
            }}
          >
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <LockOpenIcon sx={{ fontSize: 60, color: "#22c55e", mb: 2 }} />
              <Typography variant="h4" sx={{ color: "white", fontWeight: "bold", mb: 1 }}>
                Reset Password Required
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(148,163,184,0.9)" }}>
                Your account has been unlocked. Please reset your password to continue.
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, bgcolor: "rgba(211, 47, 47, 0.2)", color: "white" }}>
                {(() => {
                  if (typeof error === "string") return error;
                  if (error?.message && typeof error.message === "string") return error.message;
                  if (error?.error && typeof error.error === "string") return error.error;
                  try {
                    return JSON.stringify(error);
                  } catch {
                    return "An error occurred";
                  }
                })()}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 3, bgcolor: "rgba(34, 197, 94, 0.2)", color: "white" }}>
                {(() => {
                  if (typeof success === "string") return success;
                  if (success?.message && typeof success.message === "string") return success.message;
                  return String(success);
                })()}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "rgba(15,23,42,0.9)",
                    color: "white",
                    "& fieldset": {
                      borderColor: "rgba(51, 65, 85, 0.8)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(59, 130, 246, 0.9)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#38bdf8",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(148,163,184,0.9)",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#38bdf8",
                  },
                }}
              />
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                helperText="Must be at least 8 characters"
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "rgba(15,23,42,0.9)",
                    color: "white",
                    "& fieldset": {
                      borderColor: "rgba(51, 65, 85, 0.8)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(59, 130, 246, 0.9)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#38bdf8",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(148,163,184,0.9)",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#38bdf8",
                  },
                  "& .MuiFormHelperText-root": {
                    color: "rgba(148,163,184,0.9)",
                  },
                }}
              />
              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "rgba(15,23,42,0.9)",
                    color: "white",
                    "& fieldset": {
                      borderColor: "rgba(51, 65, 85, 0.8)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(59, 130, 246, 0.9)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#38bdf8",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(148,163,184,0.9)",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#38bdf8",
                  },
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: "linear-gradient(135deg, #2563eb 0%, #38bdf8 100%)",
                  color: "white",
                  py: 1.5,
                  fontSize: "16px",
                  textTransform: "none",
                  borderRadius: 999,
                  "&:hover": {
                    bgcolor: "linear-gradient(135deg, #1d4ed8 0%, #0ea5e9 100%)",
                  },
                  "&:disabled": { bgcolor: "rgba(37, 99, 235, 0.5)" },
                }}
              >
                {loading ? "Updating..." : "Reset Password"}
              </Button>
            </form>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
}








